export const TERMS_OF_SERVICE = `
# USER AGREEMENT / TERMS OF SERVICE
**App Name:** BrainLoop.io  
**Effective Date:** January 18, 2026

## 1. ACCEPTANCE OF TERMS
By accessing or using BrainLoop.io (the "Service"), you agree to be bound by these Terms of Service. These terms constitute a legally binding agreement between you and BrainLoop.io. If you do not agree to these terms, you are prohibited from using the Service.

## 2. ELIGIBILITY AND AGE RESTRICTIONS
The Service is intended for users who are at least 13 years of age. By creating an account, you represent and warrant that you meet this age requirement. Users under the age of 18 must have the consent of a parent or legal guardian to use the paid features of the Service.

## 3. USER ACCOUNTS AND DATA
* **Registration:** Access to certain features requires account registration using an email address and password. You are responsible for maintaining the confidentiality of your login credentials.
* **Data Hosting:** BrainLoop.io utilizes Google Firebase for data storage. By using the Service, you acknowledge that your data will be processed and stored in accordance with Google’s security protocols.
* **Account Responsibility:** You are solely responsible for all activities that occur under your account.

## 4. INTELLECTUAL PROPERTY RIGHTS
* **Ownership of Service:** BrainLoop.io, including its study algorithms (the "Loop"), user interface, logos, and original code, remains the exclusive property of BrainLoop.io.
* **User Content:** You retain ownership of the original notes and text you input into the Service. By using the Service, you grant BrainLoop.io a non-exclusive, worldwide, royalty-free license to host, store, and process your content solely for the purpose of providing the Service to you.

## 5. AI-GENERATED CONTENT (DISCLAIMER)
BrainLoop.io utilizes Large Language Models (LLMs) to assist in generating flashcards and study materials.
* **Accuracy:** AI-generated content may be inaccurate, incomplete, or biased.
* **Verification:** You are required to review and verify the accuracy of all AI-generated content. BrainLoop.io is not liable for academic errors, misinformation, or damages resulting from reliance on AI-generated materials.

## 6. PAYMENTS, SUBSCRIPTIONS, AND REFUNDS
* **Tiers:** We offer a "Freemium" tier (basic access) and a "Paid" tier (enhanced AI features).
* **Payment Processing:** All financial transactions are processed securely through Stripe. BrainLoop.io does not store full credit card details on its servers.
* **Refund Policy:** Unless required by law, all payments are non-refundable. You may cancel your subscription at any time to avoid future charges, but you will retain access to paid features until the end of your current billing period.

## 7. PROHIBITED CONDUCT
You agree not to engage in any of the following "Malicious Actions":
* Attempting to bypass security, "hack," or scrape data from the Service.
* Reverse engineering the Service’s study logic or AI prompts.
* Using the Service to generate or store harmful, illegal, or defamatory content.
* Automated use of the Service (bots) that exceeds reasonable human usage.
* Using the Service to harass or harm others.

## 8. TERMINATION OF SERVICE
We reserve the right to suspend or terminate your account immediately and without notice if we determine, in our sole discretion, that you have violated these terms, engaged in prohibited conduct, or used the Service in a manner that creates legal or financial risk for BrainLoop.io.

## 9. LIMITATION OF LIABILITY AND "AS-IS" WARRANTY
THE SERVICE IS PROVIDED ON AN "AS-IS" AND "AS-AVAILABLE" BASIS. BRAINLOOP.IO DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED. IN NO EVENT SHALL BRAINLOOP.IO BE LIABLE FOR ANY INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES, INCLUDING LOSS OF DATA OR ACADEMIC STANDING, ARISING FROM YOUR USE OF THE SERVICE.

## 10. GOVERNING LAW AND DISPUTE RESOLUTION
* **Governing Law:** This agreement is governed by the laws of the United States.
* **Mandatory Arbitration:** You agree that any dispute or claim arising from these terms shall be settled by binding arbitration rather than in a court of law. You waive any right to participate in class-action lawsuits or class-wide arbitration.
* **Severability:** If any part of these terms is found unenforceable, the remaining sections will remain in full effect.
`;

export const PRIVACY_POLICY = `
# PRIVACY POLICY
**App Name:** BrainLoop.io  
**Last Updated:** January 18, 2026

## 1. INTRODUCTION
BrainLoop.io ("we," "us," or "our") respects your privacy. This Privacy Policy explains how we collect, use, and share information when you use our web application.

## 2. INFORMATION WE COLLECT
* **Account Information:** Email address and password (stored via Google Firebase).
* **User-Generated Content:** Notes, flashcards, study logs, and quiz grades.
* **AI Interaction Data:** Prompts sent to the LLM to generate content.
* **Transaction Data:** Payment info processed via Stripe. We do not store credit card numbers.
* **Technical Data:** IP address and device info for security and monitoring.

## 3. HOW WE USE YOUR INFORMATION
We use your data to provide study features, process payments, and improve the "Loop" learning algorithm. We do not sell your personal data to third parties.

## 4. THIRD-PARTY SERVICES
We share data with:
* **Google Firebase:** For hosting and authentication.
* **Stripe:** For payment processing.
* **AI Infrastructure:** To process and generate flashcards from your notes.

## 5. DATA RETENTION & DELETION
We store data while your account is active. You may request account deletion at any time by contacting support.

## 6. CHILDREN’S PRIVACY
BrainLoop.io is not intended for children under 13. We do not knowingly collect info from children under 13.

## 7. SECURITY
We use industry-standard security via Firebase to protect your data. However, no internet transmission is 100% secure.
`;

export const COOKIE_POLICY = `
# COOKIE POLICY
**Last Updated:** January 18, 2026

## 1. HOW WE USE COOKIES
BrainLoop.io uses cookies for:
* **Authentication:** Keeping you signed in via Google Firebase.
* **Security:** Preventing fraud and protecting user data.
* **Payments:** Stripe uses cookies to process secure transactions.

## 2. YOUR CHOICES
Strictly Necessary cookies cannot be disabled as the app will not function without them. You can manage other browser settings to block non-essential cookies.
`;